import React, { useMemo } from "react";
import Skeleton from '@mui/material/Skeleton';
import { Typography } from "@mui/material";
import { useGetEnrichedTransactions } from '../../api/transactions.js';
import TransactionNetworkVisualization from "./TransactionNetworkVisualization.js";
import { max } from "date-fns";
import { perfLog, perfNow } from '../../utils/perfLog.js';

/**
* Processes bank transactions to create nodes and edges for graph visualization
* @param {Array} transactions - Array of transaction objects
* @returns {Object} - Object containing nodes and edges arrays
*/
function buildTransactionGraphData(transactions, maxNodesPerType = 10) {
    const nodes = new Map();
    const edges = new Map();

    // Helper function to create or update a node
    function updateNode(id, type, label, amount) {
        if (nodes.has(id)) {
            // nodes.get(id).amount += Math.abs(amount);
            nodes.get(id).amount += amount;
            nodes.get(id).transactionCount += 1;
        } else {
            nodes.set(id, {
                id,
                type,
                label,
                // amount: Math.abs(amount),
                amount: amount,
                transactionCount: 1
            });
        }
    }

    // Helper function to create or update an edge
    function updateEdge(sourceId, targetId, amount, edgeType = 'spending') {
        let normalizedSourceId = sourceId;
        let normalizedTargetId = targetId;

        // Ensure consistent direction for spending edges
        if (amount > 0) {
            normalizedSourceId = targetId;
            normalizedTargetId = sourceId;
        }
        const edgeId = `${normalizedSourceId}-${normalizedTargetId}`;
        if (edges.has(edgeId)) {
            edges.get(edgeId).amount += Math.abs(amount);
            edges.get(edgeId).transactionCount += 1;
        } else {
            edges.set(edgeId, {
                id: edgeId,
                source: normalizedSourceId,
                target: normalizedTargetId,
                type: edgeType,
                amount: Math.abs(amount),
                transactionCount: 1
            });
        }
    }

    // Helper function to find top N nodes by amount within each type and return a map of their IDs
    function getTopNodesByType(topN) {
        const nodesByType = {};

        // Group nodes by type
        nodes.forEach(node => {
            if (!nodesByType[node.type]) {
                nodesByType[node.type] = [];
            }
            nodesByType[node.type].push(node);
        });

        // For each type, sort nodes by amount and keep only top N
        Object.keys(nodesByType).forEach(type => {
            nodesByType[type].sort((a, b) => b.amount - a.amount);
            const topNodes = new Set(nodesByType[type].slice(0, topN).map(node => node.id));

            return topNodes;
        });

        // Combine all top nodes into a single set
        const topNodeIds = new Set();
        Object.values(nodesByType).forEach(nodeList => {
            nodeList.slice(0, topN).forEach(node => topNodeIds.add(node.id));
        });

        return topNodeIds;
    }

    const typePlurals = {
        merchant: 'Merchants',
        category: 'Categories',
        subcategory: 'Subcategories',
        account: 'Accounts'
    };

    function updateOtherNode(oldNodeId, otherNodeId) {
        const oldNode = nodes.get(oldNodeId);
        if (!oldNode) return; // If the old node doesn't exist, do nothing
        const type = oldNode.type; // e.g. "merchant"
        const label = `Other ${typePlurals[type] || type.charAt(0).toUpperCase() + type.slice(1) + 's'}`;
        const amount = oldNode.amount || 0;
        updateNode(otherNodeId, type, label, amount); // Ensure "Other" node exists
    }

    function postProcessNodesAndEdges(maxNodesPerType) {
        if (maxNodesPerType <= 0) return;

        // Identify top nodes by type
        const topNodeIds = getTopNodesByType(maxNodesPerType);

        // loop through edges and consolidate edges connected to non-top nodes into "Other" nodes
        const consolidatedEdges = new Map();
        edges.forEach(edge => {
            const sourceIsTop = topNodeIds.has(edge.source);
            const targetIsTop = topNodeIds.has(edge.target);

            let newSource = edge.source;
            let newTarget = edge.target;

            if (!sourceIsTop) {
                //newSource = `other:${edge.source.split(':')[0]}`; // e.g. "other:merchant"
                newSource = `other:${nodes.get(edge.source).type}`;
                updateOtherNode(edge.source, newSource);
            }
            if (!targetIsTop) {
                newTarget = `other:${nodes.get(edge.target).type}`;
                updateOtherNode(edge.target, newTarget);
            }

            const consolidatedEdgeId = `${newSource}-${newTarget}`;
            if (consolidatedEdges.has(consolidatedEdgeId)) {
                consolidatedEdges.get(consolidatedEdgeId).amount += edge.amount;
                consolidatedEdges.get(consolidatedEdgeId).transactionCount += edge.transactionCount;
            } else {
                consolidatedEdges.set(consolidatedEdgeId, {
                    id: consolidatedEdgeId,
                    source: newSource,
                    target: newTarget,
                    amount: edge.amount,
                    transactionCount: edge.transactionCount
                });
            }
        });
        edges.clear();
        consolidatedEdges.forEach((value, key) => edges.set(key, value));

        // Add "Other" nodes for each type if they don't already exist
        // console.warn("The following is incorrectly creating nodes without amounts. Needs to sum amounts from nodes they are replacing.");
        // const otherNodeTypes = new Set();
        // edges.forEach(edge => {
        //     if (edge.source.startsWith('other:')) {
        //         otherNodeTypes.add(edge.source);
        //     }
        //     if (edge.target.startsWith('other:')) {
        //         otherNodeTypes.add(edge.target);
        //     }
        // });

        // otherNodeTypes.forEach(otherTypeId => {
        //     if (!nodes.has(otherTypeId)) {
        //         const type = otherTypeId.split(':')[1]; // e.g. "merchant"
        //         nodes.set(otherTypeId, {
        //             id: otherTypeId,
        //             type,
        //             label: `Other ${typePlurals[type] || type.charAt(0).toUpperCase() + type.slice(1) + 's'}`,
        //             amount: 0,
        //             transactionCount: 0
        //         });
        //     }
        // });

        // Finally, remove nodes that are not in topNodeIds and not "Other" nodes
        nodes.forEach((node, nodeId) => {
            if (!topNodeIds.has(nodeId) && !nodeId.startsWith('other:')) {
                nodes.delete(nodeId);
            }
        });
    }

    if (!transactions || transactions.length === 0) {
        return { nodes: [], edges: [] };
    }




    // Process each transaction
    transactions.forEach(transaction => {
        let {
            merchantName,
            categoryName,
            subcategoryName,
            bankAccountName,
            amount
        } = transaction;

        merchantName = merchantName || 'Unknown Merchant';
        categoryName = categoryName || 'Uncategorized';
        subcategoryName = subcategoryName || 'Uncategorized';
        bankAccountName = bankAccountName || 'Unknown Account';

        // Skip if amount is 0 or missing required fields
        //if (!amount || !merchantName || !categoryName) return;

        // Create node IDs (using prefixes to avoid collisions)
        const merchantId = `merchant:${merchantName}`;
        const categoryId = `category:${categoryName}`;
        const subcategoryId = subcategoryName ? `subcategory:${subcategoryName}` : null;
        const accountId = `account:${bankAccountName}`;

        // Update nodes
        if (accountId) {
            updateNode(accountId, 'account', bankAccountName, amount);
        }

        if (merchantId) {
            updateNode(merchantId, 'merchant', merchantName, amount);
        }
        if (categoryId) {
            updateNode(categoryId, 'category', categoryName, amount);
        }

        if (subcategoryId) {
            updateNode(subcategoryId, 'subcategory', subcategoryName, amount);
        }

        // Create edges
        // Account -> Merchant (money flow)
        if (accountId && merchantId) {
            updateEdge(accountId, merchantId, amount, 'payment');
        }

        // Merchant -> Category (categorization)
        if (merchantId && categoryId) {
            updateEdge(categoryId, merchantId, amount, 'categorized_as');
        }

        // Category -> Subcategory (if subcategory exists)
        if (subcategoryId && categoryId) {
            updateEdge(categoryId, subcategoryId, amount, 'subcategorized_as');
        }
        // Also create direct merchant -> subcategory edge
        if (merchantId && subcategoryId) {
            updateEdge(subcategoryId, merchantId, amount, 'subcategorized_as');
        }

        // Account -> Category (direct spending by category)
        if (accountId && categoryId) {
            updateEdge(accountId, categoryId, amount, 'spent_on');
        }

        if (subcategoryId && accountId) {
            // Account -> Subcategory (direct spending by subcategory)
            updateEdge(accountId, subcategoryId, amount, 'spent_on');
        }
    });

    postProcessNodesAndEdges(maxNodesPerType);

    return {
        nodes: Array.from(nodes.values()),
        edges: Array.from(edges.values())
    };
}

const TransactionNodeChart = ({ transactions, isLoading, maxNodesPerType = 10 }) => {
    //const { data: transactions = [], error, isLoading } = useGetEnrichedTransactions();



    // Memoize the graph build: without this it ran on EVERY render (any
    // display-option / filter-description change, etc.) and rebuilt the full
    // node/edge maps over all rows. Key on the transactions array reference +
    // maxNodesPerType so we only rebuild when the input actually changes — the
    // sort/filter/optimistic-update cascade no longer pays the full-row cost on
    // unrelated re-renders.
    const graphData = useMemo(
        () => {
            // Diagnostic-only (gated, primitives only): graph-build cost + how
            // often it rebuilds (now only on real input change).
            const _t0 = perfNow();
            const result = buildTransactionGraphData(transactions, maxNodesPerType);
            perfLog('view.graphBuild', {
                rows: Array.isArray(transactions) ? transactions.length : 0,
                nodes: result.nodes.length,
                edges: result.edges.length,
                ms: +(perfNow() - _t0).toFixed(1),
            });
            return result;
        },
        [transactions, maxNodesPerType],
    );

    // Placeholder for the actual chart implementation
    if (isLoading) {
        return <Skeleton variant="rectangular" width="100%" height={400} />;
    }

    if (graphData.nodes.length === 0) {
        console.log("No nodes to display in the graph.");
        return null;
    }
    return <TransactionNetworkVisualization graphData={graphData} />;
};

export default TransactionNodeChart;